import { Component, OnInit, inject, signal } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { AssetService } from "../../../../services/api/asset/asset.service";
import { filter, first } from "rxjs";
import { ASSET_TYPES, AssetType } from "../../../../enums/assetTypes.enum";
import { SvgIconComponent } from "../../../../shared/components/svg-icon/svg-icon.component";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Table, TableModule } from "@openng/optimus-ui/table";
import { IconFieldModule } from "@openng/optimus-ui/iconfield";
import { InputIconModule } from "@openng/optimus-ui/inputicon";
import { InputTextModule } from "@openng/optimus-ui/inputtext";
import { DialogService, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { GateComponent } from "../forms/gate/gate.component";
import { CustomerComponent } from "../forms/customer/customer.component";
import { LocationComponent } from "../forms/location/location.component";
import { BuildingComponent } from "../forms/building/building.component";
import { ConfirmationService } from "@openng/optimus-ui/api";
import { MultiSelectModule } from "@openng/optimus-ui/multiselect";
import { FormsModule } from "@angular/forms";
import { YesNoPipe } from "../../../../shared/pipes/yesNo/yes-no.pipe";
import { ComponentsListComponent } from "../components-list/components-list.component";
import { UserListComponent } from "../user-list/user-list.component";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { HasPermissionDirective } from "../../../../shared/directives/has-permission/has-permission.directive";
import { PermissionService } from "../../../../services/api/permission/permission.service";
import { createTimeoutSignal } from "../../../../shared/utils/timeout.utils";
import { TimeoutComponent } from "../../../../shared/components/timeout/timeout.component";
import { SimplebarAngularModule } from "simplebar-angular";
import { BotevoAssetCompact, BotevoAsset } from "../../../../models/api/asset/botevoAsset";
import { Building } from "../../../../models/api/asset/building";
import { Location } from "../../../../models/api/asset/location";
import { Customer } from "../../../../models/api/asset/customer";
import { GateSystem, GateSystemDTO } from "../../../../models/api/asset/gateSystem";
import { Tenant } from "../../../../models/api/tenant";
import { Column } from "../../../../models/ui/tableColumn";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { getAssetChildrenType } from "../../../../shared/utils/assetType.utils";
import {
  ThiefProtectionConfigResponse,
  ThiefProtectionService,
} from "../../../../services/api/thief-protection/thief-protection.service";
import { BuildingOpenTimesComponent } from "../forms/building-open-times/building-open-times.component";
import { HardwareConfigurationDialogComponent } from "../hardware-configuration-dialog/hardware-configuration-dialog.component";

export const CUSTOMER_COLS: Column[] = [
  { field: "label", header: "gate_administration.asset_table.label" },
  { field: "notes", header: "gate_administration.asset_table.notes" },
];
export const LOCATION_COLS: Column[] = [
  { field: "label", header: "gate_administration.asset_table.label" },
  { field: "address", subField: "street", header: "common.address.street" },
  { field: "address", subField: "number", header: "common.address.number" },
  { field: "address", subField: "city", header: "common.address.city" },
  { field: "address", subField: "zipCode", header: "common.address.zipCode" },
  { field: "notes", header: "gate_administration.asset_table.notes" },
];
export const BUILDING_COLS: Column[] = [
  { field: "label", header: "gate_administration.asset_table.label" },
  { field: "address", subField: "street", header: "common.address.street" },
  { field: "address", subField: "number", header: "common.address.number" },
  { field: "address", subField: "city", header: "common.address.city" },
  { field: "address", subField: "zipCode", header: "common.address.zipCode" },
  { field: "notes", header: "gate_administration.asset_table.notes" },
];
export const GATE_SYSTEM_COLS: Column[] = [
  { field: "label", header: "gate_administration.asset_table.label" },
  { field: "coreId", header: "gate_administration.asset_table.coreId" },
  {
    field: "personalDoor",
    header: "gate_administration.asset_table.personalDoor",
  },
];

@Component({
  standalone: true,
  selector: "app-asset-details",
  imports: [
    SvgIconComponent,
    TableModule,
    IconFieldModule,
    InputTextModule,
    InputIconModule,
    TranslateModule,
    MultiSelectModule,
    FormsModule,
    YesNoPipe,
    ProgressSpinnerModule,
    HasPermissionDirective,
    TimeoutComponent,
    SimplebarAngularModule,
  ],
  templateUrl: "./asset-details.component.html",
  styleUrl: "./asset-details.component.scss",
  providers: [],
})
export class AssetDetailsComponent implements OnInit {
  public tableTitle!: string;
  public translate = inject(TranslateService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private assetService = inject(AssetService);
  private dialogService = inject(DialogService);
  private confirmationService = inject(ConfirmationService);
  private thiefProtectionService = inject(ThiefProtectionService);

  public permissions = inject(PermissionService);

  public cols!: Column[];
  public selectedCols!: Column[];
  public assetType!: AssetType;
  public assetId!: string;
  public assets!: BotevoAssetCompact[];

  public loadingAssetTable = signal(false);
  public timeoutAssetTable = createTimeoutSignal(this.loadingAssetTable);

  public loadingAssetDetails = signal(false);
  public timeoutAssetDetails = createTimeoutSignal(this.loadingAssetDetails);

  public allAssetTypes = ASSET_TYPES;

  public currentSelectedAsset!: BotevoAsset;
  private ref: DynamicDialogRef | null = null;

  public openTimesRef: DynamicDialogRef | null = null;

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe({
        next: () => {
          const assetId = this.route.snapshot.params["id"];
          const assetType = this.route.snapshot.queryParams["assetType"];
          if (assetId && assetType) {
            this.ref = null;
            this.assetId = assetId;
            this.assetType = assetType;
            this.setAssetTableData(this.assetType, this.assetId);
            this.setCurrentSelectedAsset(this.assetId, this.assetType);
          }
        },
      });
  }

  ngOnInit(): void {
    const assetId = this.route.snapshot.params["id"];
    const assetType = this.route.snapshot.queryParams["assetType"];

    if (assetId && assetType) {
      this.assetId = assetId;
      this.assetType = assetType;
      this.setAssetTableData(this.assetType, this.assetId);
      this.setCurrentSelectedAsset(this.assetId, this.assetType);
    }
  }

  /**
   * Set the asset table data.
   * @param assetType The type of the parent asset.
   * @param parentId The id of the parent asset.
   */
  private setAssetTableData(assetType: AssetType, parentId?: string) {
    this.loadingAssetTable.set(true);
    const childrenType = getAssetChildrenType(assetType);
    if (childrenType) {
      this.getAssetChildren(childrenType, parentId, assetType);
      this.cols = this.getCols(childrenType);
      this.selectedCols = this.getCols(childrenType);
      this.tableTitle = this.getTitle(childrenType, true);
    }
  }

  /**
   * Gets the children assets for the given asset type.
   *
   * @param childrenType The type of children assets to fetch.
   * @param parentId The id of the parent asset.
   * @param parentType The type of the parent asset.
   * @param refresh If true, refresh the asset hierarchy.
   */
  private getAssetChildren(childrenType: string | null, parentId?: string, parentType?: AssetType, refresh = false) {
    this.loadingAssetTable.set(true);
    this.assetService
      .getAssets(childrenType, parentId, parentType, refresh)
      .pipe(first())
      .subscribe({
        next: (assets: BotevoAssetCompact[]) => {
          if (assets) {
            this.assets = assets;
            this.loadingAssetTable.set(false);
          }
        },
      });
  }

  /**
   * Set the currently selected asset.
   * @param assetId The id of the asset to select.
   * @param assetType The type of the asset to select.
   * @param refresh Optional. Whether to refresh the asset data. Defaults to false.
   */
  private setCurrentSelectedAsset(assetId: string, assetType: AssetType) {
    this.loadingAssetDetails.set(true);
    this.assetService
      .getAssetDetails(assetId, assetType, true)
      .pipe(first())
      .subscribe({
        next: (asset) => {
          if (asset) {
            this.currentSelectedAsset = asset;
          }

          this.loadingAssetDetails.set(false);
        },
      });
  }

  /**
   * Open the asset form to add a new asset of the given type.
   * @param assetType The type of asset to add.
   */
  public onCreateClick(assetType: AssetType | null) {
    if (assetType) {
      this.assetService
        .getEmptyAsset(assetType, this.currentSelectedAsset)
        .pipe(first())
        .subscribe({
          next: (asset) => {
            if (asset) {
              this.openAssetForm(assetType, asset, "add");
            }
          },
        });
    }
  }

  /**
   * Open the asset form for editing the given asset.
   * @param assetType The type of the asset to edit.
   * @param assetId The id of the asset to edit.
   * @param clickFromChildAsset Whether the edit button was clicked on a child asset.
   */
  public onEditClick(assetType: AssetType | null, assetId: string | undefined, clickFromChildAsset: boolean) {
    if (assetType && assetId) {
      if (clickFromChildAsset) {
        this.assetService
          .getAssetDetails(assetId, assetType)
          .pipe(first())
          .subscribe({
            next: (asset) => {
              if (asset) {
                this.openAssetForm(assetType, asset, "edit", clickFromChildAsset);
              }
            },
          });
      } else {
        this.openAssetForm(assetType, this.currentSelectedAsset, "edit", clickFromChildAsset);
      }
    }
  }

  /**
   * Open the thief protection configuration dialog for the given building
   * @param id The id of the building to edit
   */
  public onEditOpenTimesClick(id?: string) {
    const targetId = id ?? this.assetId;
    if (targetId) {
      this.thiefProtectionService.getThiefProtectionConfig(targetId).subscribe({
        next: (response) => {
          if (response) {
            this.openOpenTimesForm(response, targetId);
          }
        },
      });
    }
  }

  /**
   * Opens a dialog for editing a thief protection configuration
   * @param response The initial configuration that should be edited
   */
  private openOpenTimesForm(response: ThiefProtectionConfigResponse, buildingId: string) {
    this.openTimesRef = this.dialogService.open(BuildingOpenTimesComponent, {
      header: this.translate.instant("gate_administration.open_times_form.title"),
      modal: true,
      width: "68vw",
      height: "70vh",
      contentStyle: { overflow: "auto" },
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      data: { buildingId, response: response },
      closable: true,
    });
  }

  /**
   * Delete the asset of given type and id.
   * @param event The event that triggered the deletion.
   * @param assetId The id of the asset to be deleted.
   * @param assetType The type of the asset to be deleted.
   */
  public onDeleteClick(event: Event, assetId: string | undefined, assetType: AssetType | null) {
    let confirmMessage;
    switch (assetType) {
      case ASSET_TYPES.LOCATION:
        confirmMessage = "confirm_location";
        break;
      case ASSET_TYPES.BUILDING:
        confirmMessage = "confirm_building";
        break;
      case ASSET_TYPES.GATE_SYSTEM:
        confirmMessage = "confirm_gate_system";
        break;
      default:
        confirmMessage = "confirm";
        break;
    }
    if (assetId && assetType && event.currentTarget) {
      this.confirmationService.confirm({
        target: event.currentTarget,
        message: this.translate.instant("gate_administration.asset_table." + confirmMessage),
        key: "global",
        accept: () => {
          this.assetService
            .deleteAsset(assetType, assetId)
            .pipe(first())
            .subscribe({
              next: (res) => {
                if (res.success) {
                  this.setCurrentSelectedAsset(this.assetId, this.assetType);
                  this.getAssetChildren(getAssetChildrenType(this.assetType), this.assetId, this.assetType, true);
                }
              },
            });
        },
        reject: () => {
          /*empty*/
        },
      });
    }
  }

  /**
   * Duplicate the given GateSystem and open the asset form to add it.
   * @param gateSystem The GateSystem to duplicate.
   */
  public onDuplicateClick(gateSystem: GateSystemDTO) {
    if (gateSystem) {
      const gateSystemValues = { ...gateSystem };
      delete gateSystemValues.coreId;
      delete gateSystemValues.id;
      delete gateSystemValues.gateHistoryCycle;
      gateSystemValues.label = "";
      const gateSystemCopy = new GateSystem(gateSystemValues);

      this.openAssetForm(ASSET_TYPES.GATE_SYSTEM, gateSystemCopy, "add", true);
    }
  }

  /**
   * Opens a dialog displaying the list of components for the specified gate system.
   * @param gateSystem The gate system whose components will be listed.
   */

  public onComponentsListClick(gateSystem: GateSystem) {
    if (gateSystem) {
      this.ref = this.dialogService.open(ComponentsListComponent, {
        header: this.translate.instant("gate_administration.components_list.title"),
        modal: true,
        width: "50vw",
        contentStyle: { overflow: "auto" },
        breakpoints: {
          "960px": "75vw",
          "640px": "90vw",
        },
        data: { gateSystem },
        closable: true,
      });
    }
  }

  /**
   * Opens the hardware configuration dialog for a gate system.
   * @param gateSystemId The id of the gate system to configure.
   */
  public onHardwareConfigurationClick(gateSystemId: string): void {
    this.ref = this.dialogService.open(HardwareConfigurationDialogComponent, {
      header: this.translate.instant("gate_administration.hardware_configuration.title"),
      modal: true,
      width: "70vw",
      contentStyle: { overflow: "auto" },
      breakpoints: {
        "960px": "85vw",
        "640px": "95vw",
      },
      data: { gateSystemId },
      closable: true,
    });
  }

  /**
   * Opens a dialog for managing users of the currently selected asset.
   * This method is only called if the currently selected asset is a customer.
   */
  public onUserListClick() {
    if (this.currentSelectedAsset) {
      this.ref = this.dialogService.open(UserListComponent, {
        header: this.translate.instant("gate_administration.user_list.title"),
        modal: true,
        width: "50vw",
        contentStyle: { overflow: "auto" },
        breakpoints: {
          "960px": "75vw",
          "640px": "90vw",
        },
        data: { customerId: this.currentSelectedAsset.id },
        closable: true,
      });
    }
  }

  /**
   * Opens a dialog for adding or editing an asset, based on the given parameters.
   * @param formType The type of asset to create or edit.
   * @param asset The asset to edit, or undefined if a new asset is to be created.
   * @param actionType Whether to add or edit the asset.
   * @param clickFromChildAsset Whether the click that opened this dialog came from a child asset.
   */
  private openAssetForm(formType: AssetType, asset: BotevoAsset, actionType: string, clickFromChildAsset = false) {
    this.ref = this.dialogService.open<CustomerComponent | LocationComponent | BuildingComponent | GateComponent>(
      this.getAssetTypeForm(formType),
      {
        header: this.translate.instant(
          "gate_administration.dialog_title." + actionType + "." + this.getTitle(formType, true)
        ),
        modal: true,
        width: "70vw",
        contentStyle: { overflow: "auto" },
        breakpoints: {
          "960px": "75vw",
          "640px": "90vw",
        },
        data: { asset, actionType },
        closable: true,
      }
    );
    this.ref?.onClose.pipe(first()).subscribe({
      next: (success: true) => {
        this.onDialogClosed(success, clickFromChildAsset, actionType);
      },
    });
  }

  /**
   * Refreshes the asset tree and the current selected asset after a dialog is closed.
   * @param success Whether the dialog was closed successfully.
   * @param clickFromChildAsset Whether the dialog was opened from a child asset.
   * @param actionType The type of action that was performed in the dialog.
   */
  private onDialogClosed(success: boolean, clickFromChildAsset: boolean, actionType: string) {
    if (success) {
      if (clickFromChildAsset || actionType === "add") {
        this.getAssetChildren(getAssetChildrenType(this.assetType), this.assetId, this.assetType, true);
        this.setCurrentSelectedAsset(this.assetId, this.assetType);
      } else {
        this.setCurrentSelectedAsset(this.assetId, this.assetType);
      }
    }
  }

  /**
   * Return an array of column fields for filtering, considering subFields.
   * @param columns The columns to get the fields from.
   * @returns An array of fields or undefined if columns is undefined.
   */
  public getFilterFields(columns: Column[]): string[] | undefined {
    return columns.map((column) => {
      return column.subField ? `${column.field}.${column.subField}` : column.field;
    });
  }

  /**
   * Checks if the given asset is a Tenant.
   * @param asset The asset to check.
   * @returns True if the asset is a Tenant, false otherwise.
   */
  public isTenant(asset: BotevoAsset): asset is Tenant {
    return asset instanceof Tenant;
  }

  /**
   * Checks if the given asset is a Customer.
   * @param asset The asset to check.
   * @returns True if the asset is a Customer, false otherwise.
   */
  public isCustomer(asset: BotevoAsset): asset is Customer {
    return asset instanceof Customer;
  }

  /**
   * Checks if the given asset is a Location.
   * @param asset The asset to check.
   * @returns True if the asset is a Location, false otherwise.
   */
  public isLocation(asset: BotevoAsset): asset is Location {
    return asset instanceof Location;
  }

  /**
   * Checks if the given asset is a Building.
   * @param asset The asset to check.
   * @returns True if the asset is a Building, false otherwise.
   */
  public isBuilding(asset: BotevoAsset): asset is Building {
    return asset instanceof Building;
  }

  /**
   * Checks if the given asset is a GateSystem.
   * @param asset The asset to check.
   * @returns True if the asset is a GateSystem, false otherwise.
   */
  public isGateSystem(asset: BotevoAsset): asset is GateSystem {
    return asset instanceof GateSystem;
  }

  /**
   * Get the component to use for editing/adding assets of the given type.
   * @param assetType The type of asset to edit/add.
   * @returns The component to use.
   */
  public getAssetTypeForm(assetType: AssetType) {
    switch (assetType) {
      case ASSET_TYPES.CUSTOMER:
        return CustomerComponent;
      case ASSET_TYPES.LOCATION:
        return LocationComponent;
      case ASSET_TYPES.BUILDING:
        return BuildingComponent;
      case ASSET_TYPES.GATE_SYSTEM:
        return GateComponent;
      default:
        return GateComponent;
    }
  }

  /**
   * Get the type of children assets for the given asset type.
   * @param assetType The type of the parent asset.
   * @returns The type of children assets if it exists, otherwise null.
   */
  public getAssetChildrenType(assetType: AssetType): AssetType | null {
    return getAssetChildrenType(assetType);
  }

  /**
   * Filter the asset table.
   * @param dt The table to filter.
   * @param event The event containing the target input element.
   */
  public filterGlobal(dt: Table, event: Event) {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    dt.filterGlobal(value, "contains");
  }

  /**
   * Return the translated title for the given asset type, in plural form if requested.
   * @param assetType the type of asset
   * @param plural whether to return the plural form
   * @returns the translated title
   */
  public getTitle(assetType: AssetType, plural: boolean): string {
    switch (assetType) {
      case ASSET_TYPES.TENANT:
        return plural ? "tenants" : "tenant";
      case ASSET_TYPES.CUSTOMER:
        return plural ? "customers" : "customer";
      case ASSET_TYPES.LOCATION:
        return plural ? "locations" : "location";
      case ASSET_TYPES.BUILDING:
        return plural ? "buildings" : "building";
      case ASSET_TYPES.GATE_SYSTEM:
        return plural ? "gate_systems" : "gate_system";
      default:
        return plural ? "gate_systems" : "gate_system";
    }
  }

  /**
   * Get column definitions for the asset table based on asset type
   * @param assetType Asset type
   * @returns Column definitions
   */
  private getCols(assetType: AssetType) {
    switch (assetType) {
      case ASSET_TYPES.CUSTOMER:
        return CUSTOMER_COLS;
      case ASSET_TYPES.LOCATION:
        return LOCATION_COLS;
      case ASSET_TYPES.BUILDING:
        return BUILDING_COLS;
      case ASSET_TYPES.GATE_SYSTEM:
        return GATE_SYSTEM_COLS;
      default:
        return CUSTOMER_COLS;
    }
  }
}
